import {
  AlertIcon,
  Banner,
  Button,
  Flex,
  Modal,
  Typography,
} from '@iheartjane/reefer';
import { Form, useForm } from '@iheartjane/reefer-hook-form';
import type { FontFamily } from '@iheartjane/types';
import { useMaskito } from '@maskito/react';
import { useState } from 'react';

import { isValidUrl, urlMask } from '@jane/business-admin/util';
import { validateFont } from '@jane/shared/util';

type FontImportFormData = {
  fontFamily: string;
  fontUrl: string;
};

const EMPTY_FORM: FontImportFormData = { fontFamily: '', fontUrl: '' };

const LOAD_ERROR =
  "We couldn't load that font. Check the URL and font family name, and try again.";

const trimmed = (value: unknown) => String(value ?? '').trim();

export const FontImportModal = ({
  open,
  onChange,
  onRequestClose,
}: {
  onChange: (font: FontFamily) => void;
  onRequestClose: () => void;
  open: boolean;
}) => {
  const urlRef = useMaskito({ options: urlMask });
  const [error, setError] = useState<string | undefined>(undefined);
  const formMethods = useForm<FontImportFormData>({
    defaultValues: EMPTY_FORM,
  });

  const handleSubmit = async ({ fontFamily, fontUrl }: FontImportFormData) => {
    const font: FontFamily = {
      font_family: trimmed(fontFamily),
      url: trimmed(fontUrl),
    };

    const validFont = await validateFont({
      fontFamily: font.font_family,
      url: font.url,
    });

    if (!validFont) {
      setError(LOAD_ERROR);
      return;
    }

    onChange(font);
    handleClose();
  };

  const handleClose = () => {
    formMethods.reset(EMPTY_FORM);
    setError(undefined);
    onRequestClose();
  };

  return (
    <Modal
      contentLabel="Custom font"
      onRequestClose={onRequestClose}
      open={open}
      variant="dialogue"
    >
      <Form.BaseForm
        formMethods={formMethods}
        name="Custom font"
        onSubmit={handleSubmit}
      >
        <Modal.Content>
          <Flex flexDirection="column" gap={16} maxWidth={432}>
            <Typography variant="h2-semibold">Custom font</Typography>

            <Typography>
              Paste a font stylesheet URL and family name to load your custom
              font.
            </Typography>

            {error && (
              <Banner full icon={<AlertIcon />} label={error} variant="error" />
            )}

            <Form.TextField
              errorOnBlur
              label="Font URL"
              name="fontUrl"
              onChange={() => setError(undefined)}
              ref={(el) => {
                formMethods.register('fontUrl').ref(el);
                urlRef(el);
              }}
              validate={(value) =>
                isValidUrl(trimmed(value)) || 'Enter a valid URL'
              }
            />

            <Form.TextField
              errorOnBlur
              label="Font family"
              name="fontFamily"
              onChange={() => setError(undefined)}
              validate={(value) =>
                Boolean(trimmed(value)) || 'Enter a font family name.'
              }
            />

            <Flex gap={16} mt={16}>
              <Button
                full
                variant="tertiary"
                label="Cancel"
                onClick={handleClose}
              />
              <Form.SubmitButton full variant="primary" label="Import" />
            </Flex>
          </Flex>
        </Modal.Content>
      </Form.BaseForm>
    </Modal>
  );
};
